<template>
  <Layout>
    <div class="schedule-wrapper">
      <StellarBackground backColor="#2d3434" :circleOffset="{x: -250, y: -150}"/>
      <div class="schedule-container">
        <CyberLoading :is-loading="isLoading" />
        <div class="schedule-page" :class="{ 'content-loaded': !isLoading }">
          <div class="schedule-header">
            <h1>{{ t('schedule.title') }}</h1>
            <p>{{ t('schedule.subtitle') }}</p>
          </div>
          <div class="legend-container">
            <div class="legend-title">{{ t('schedule.legend') }}</div>
            <a class="stream-link" :href="t('schedule.streamLink')" target="_blank">{{ t('schedule.streamText') }}</a>
          </div>
          <div class="timeline">
            <div class="timeline-header">
              <div class="day-label-spacer"></div>
            </div>
            <div class="days-container">
              <div v-for="day in days" :key="day.id" class="day-container">
                <div class="day-label">
                  <div class="day-label-content">{{ day.label }}</div>
                </div>
                <div class="day-content" 
                     @mousedown="startDragging"
                     @mousemove="onDrag"
                     @mouseup="stopDragging"
                     @mouseleave="stopDragging">
                  <div class="day-timeline" :style="getGridStyle(day.id)">
                    <div 
                      v-for="hour in getHoursForDay(day.id)" 
                      :key="hour" 
                      class="timeline-hour"
                    >
                      {{ formatHour(hour) }}
                    </div>
                  </div>
                  <div class="time-grid" :style="getGridStyle(day.id)">
                    <div 
                      v-for="hour in getHoursForDay(day.id)" 
                      :key="hour" 
                      class="time-grid-line"
                    ></div>
                  </div>
                  <div class="event-rows">
                    <template v-for="(row, rowIndex) in getEventRowsForDay(day.id)" :key="rowIndex">
                      <div class="event-row">
                        <div
                          v-for="event in row"
                          :key="event.id"
                          class="event-card"
                          :style="getEventStyle(event)"
                        >
                          <div class="event-content">
                            <div class="event-header">
                              <h3>{{ event.title }}</h3>
                            </div>
                            <p>{{ event.startTime }} - {{ event.endTime }}</p>
                          </div>
                          <div class="hover-card" :class="{ 
                            'align-right': shouldAlignRight(event),
                            'align-left': !shouldAlignRight(event),
                            'align-bottom': isBottomRow(event, rowIndex, day)
                          }">
                            <div class="hover-card-content">
                              <h3 class="hover-title">{{ event.title }}</h3>
                              <div class="hover-time">
                                <v-icon size="small" color="#76c4ae">mdi-clock-outline</v-icon>
                                <span>{{ event.startTime }} - {{ event.endTime }}</span>
                              </div>
                              <div class="hover-description">{{ event.description }}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- <div class="big-highlight">
            <div class="highlight-content">
              <h2>{{ t('schedule.soon') }}</h2>
            </div>
          </div> -->
        </div>
        <div class="bottom-links d-none d-md-flex">
          <a href="https://alterlan.be/privacy" target="_blank" rel="noopener noreferrer" class="nav-link" v-html="t('menu.footerLinks.privacy')"></a>
            <a href="https://alterlan.be/terms" target="_blank" rel="noopener noreferrer" class="nav-link" v-html="t('menu.footerLinks.terms')"></a>
            <router-link to="/contact" class="nav-link" v-html="t('menu.footerLinks.contact')"></router-link>
        </div>
      </div>
    </div>   
  </Layout>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import Layout from '@/components/layout/Layout.vue'
import CyberLoading from '@/components/ui/CyberLoading.vue'
import StellarBackground from '@/components/animations/StellarBackground.vue'

const { t, tm } = useI18n()
const isLoading = ref(true)
const debug = ref(true)
const isDragging = ref(false)
const startX = ref(0)
const scrollLeft = ref(0)

onMounted(() => {
  // Simulate loading time for dramatic effect
  setTimeout(() => {
    isLoading.value = false
  }, 1300)
})

const days = computed(() => [
  { id: t('schedule.days.day1'), label: t('schedule.days.day1') },
  { id: t('schedule.days.day2'), label: t('schedule.days.day2') },
  { id: t('schedule.days.day3'), label: t('schedule.days.day3') }
])

// Helper function to format hours in 24h format
const formatHour = (hour: number): string => {
  return `${hour.toString().padStart(2, '0')}:00`
}

// Convert time string to decimal hours (e.g., "14:30" -> 14.5)
const timeToDecimal = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number)
  return hours + (minutes / 60)
}

// Check if two events overlap
const eventsOverlap = (event1: any, event2: any): boolean => {
  const start1 = timeToDecimal(event1.startTime)
  const end1 = timeToDecimal(event1.endTime)
  const start2 = timeToDecimal(event2.startTime)
  const end2 = timeToDecimal(event2.endTime)

  return (start1 < end2 && end1 > start2)
}

// Organize events into rows to avoid overlaps
const getEventRowsForDay = (dayId: string) => {
  const dayEvents = getAllEvents.value.filter(event => event.date === dayId)
  const rows: any[][] = []

  // Sort events by start time
  dayEvents.sort((a, b) => timeToDecimal(a.startTime) - timeToDecimal(b.startTime))

  dayEvents.forEach(event => {
    // Find the first row where the event doesn't overlap with any existing events
    let rowIndex = 0
    let placed = false

    while (!placed) {
      if (!rows[rowIndex]) {
        rows[rowIndex] = []
        rows[rowIndex].push(event)
        placed = true
      } else {
        const hasOverlap = rows[rowIndex].some(existingEvent => 
          eventsOverlap(event, existingEvent)
        )

        if (!hasOverlap) {
          rows[rowIndex].push(event)
          placed = true
        } else {
          rowIndex++
        }
      }
    }
  })

  if (debug.value) {
    console.log(`Rows for day ${dayId}:`, rows)
  }

  return rows
}

const isBottomRow = (event: any, rowIndex: number, day: any) => {
  const rows = getEventRowsForDay(day.id)
  return rowIndex === rows.length - 1
}

// Get all events from the translations
const getAllEvents = computed(() => {
  const timeline = tm('schedule.timeline')
  return Object.entries(timeline).map(([id, event]: [string, any]) => ({
    id,
    title: event.title,
    date: event.date,
    startTime: event.startTime,
    endTime: event.endTime,
    description: event.description
  }))
})

// Get hours array based on day
const getHoursForDay = (dayId: string) => {
  if (dayId === t('schedule.days.day1')) {
    // Friday: show hours from 18:00 to 23:00
    return Array.from({ length: 6 }, (_, i) => i + 18)
  } else if (dayId === t('schedule.days.day2')) {
    // Saturday: show hours from 08:00 to 23:00
    return Array.from({ length: 16 }, (_, i) => i + 8)
  } else {
    // Sunday: show hours from 08:00 to 18:00
    return Array.from({ length: 10 }, (_, i) => i + 8)
  }
}

// Get grid style based on number of hours
const getGridStyle = (dayId: string) => {
  const hours = getHoursForDay(dayId).length
  return {
    gridTemplateColumns: `repeat(${hours}, 150px)`,
    width: `${hours * 150}px`
  }
}

// Calculate event position and width based on time
const getEventStyle = (event: any) => {
  let startDecimal = timeToDecimal(event.startTime)
  let endDecimal = timeToDecimal(event.endTime)
  
  // Handle events that cross midnight
  if (endDecimal < startDecimal) {
    endDecimal += 24
  }
  
  // Calculate duration
  const duration = endDecimal - startDecimal

  // Adjust position based on day
  let adjustedStart = startDecimal
  if (event.date === t('schedule.days.day1')) {
    // Friday: subtract 18 hours to align with grid
    adjustedStart = startDecimal - 18
  } else {
    // Saturday and Sunday: subtract 8 hours to align with grid
    adjustedStart = startDecimal - 8
  }

  const hourWidth = ref(150)
  const left = `${(adjustedStart * hourWidth.value)}px`
  const width = `${(duration * hourWidth.value)}px`

  return {
    left,
    width,
  }
}

const shouldAlignRight = (event: any) => {
  const startDecimal = timeToDecimal(event.startTime)

  if (event.date === t('schedule.days.day1')) {
    // Friday: align right if starts between 18:00 and 21:00
    return startDecimal >= 18 && startDecimal <= 21
  } else if (event.date === t('schedule.days.day2')) {
    // Saturday: align right if starts between 08:00 and 11:00
    return startDecimal >= 8 && startDecimal <= 11
  }
  return false;
}

const startDragging = (e: MouseEvent) => {
  const target = e.currentTarget as HTMLElement
  isDragging.value = true
  startX.value = e.pageX - target.offsetLeft
  scrollLeft.value = target.scrollLeft
  target.style.cursor = 'grabbing'
  // Prevent text selection during drag
  e.preventDefault()
}

const stopDragging = (e: MouseEvent) => {
  if (!isDragging.value) return
  const target = e.currentTarget as HTMLElement
  isDragging.value = false
  target.style.cursor = 'grab'
}

const onDrag = (e: MouseEvent) => {
  if (!isDragging.value) return
  const target = e.currentTarget as HTMLElement
  const x = e.pageX - target.offsetLeft
  const walk = (x - startX.value) * 1.5 // Multiply by 1.5 for faster scrolling
  target.scrollLeft = scrollLeft.value - walk
}

onMounted(() => {
  const calculateHourWidth = () => {
    const timelineHour = document.querySelector('.timeline-hour')
    if (timelineHour) {
      const width = timelineHour.getBoundingClientRect().width
      // Store the width in a reactive variable or use a method to update event styles
      getEventStyle.hourWidth = width
    }
  }

  // Calculate on mount and add resize listener
  calculateHourWidth()
})
</script>

<style scoped>
.schedule-wrapper {
  position: relative;
  min-height: 80vh;
  width: 100%;
  background-color: transparent;
  overflow: visible;
}

.schedule-container {
  position: relative;
  width: 100%;
  min-height: 80vh;
  padding: 0.5rem;
  z-index: 1;
  overflow: visible;
}

.schedule-page {
  position: relative;
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.5s ease, transform 0.5s ease;
  z-index: 1;
}

.content-loaded {
  opacity: 1;
  transform: translateY(0);
}

.schedule-header {
  text-align: center;
  margin-bottom: 2rem;
  margin-top: 2rem;
  padding: 2rem 0;
  position: relative;
  z-index: 1;
}

.schedule-header h1 {
  font-family: 'Airborne GP', sans-serif;
  font-size: 3.5rem;
  font-weight: bold;
  text-transform: uppercase;
  color: #76c4ae;
  letter-spacing: 4px;
  position: relative;
  display: inline-block;
  margin-bottom: 1rem;
}

.schedule-header p {
  font-size: 1.2rem;
  width: 90%;
  color: #ccc;
  margin: 0 auto;
  line-height: 1.6;
  position: relative;
  text-shadow: 0 0 10px rgba(255, 255, 255, 0.1);
}

.stream-link {
  font-family: 'Poppins', sans-serif;
  font-size: 1.0rem;
  color: rgba(200, 117, 69, 1);
  text-decoration: none;
  transition: all 0.3s ease;

}

.stream-link:hover {
  text-decoration: underline;
}

/* Tablet */
@media (max-width: 1024px) {
  .schedule-header {
    margin-bottom: 3rem;
    padding: 1.5rem 0;
  }

  .schedule-header h1 {
    font-size: 2.8rem;
    letter-spacing: 3px;
  }

  .schedule-header p {
    font-size: 1.1rem;
    width: 95%;
  }
}

/* Mobile */
@media (max-width: 767px) {
  .schedule-header {
    margin-bottom: 2rem;
    padding: 1rem 0;
  }

  .schedule-header h1 {
    font-size: 1.9rem;
    letter-spacing: 2px;
  }

  .schedule-header p {
    font-size: 1rem;
    width: 100%;
    padding: 0 1rem;
  }
}

/* Small Mobile */
@media (max-width: 425px) {
  .schedule-header {
    margin-bottom: 1.5rem;
  }

  .schedule-header h1 {
    font-size: 1.5rem;
    letter-spacing: 1px;
  }

  .schedule-header p {
    font-size: 0.9rem;
  }

  .schedule-container {
    padding: 0rem !important;
  }
}

::-webkit-scrollbar{
    height: 4px;
}

::-webkit-scrollbar-thumb:horizontal{
    background: #76c4ae;
}


.timeline {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  max-width: 100vw;
}

.timeline-header {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
}

.day-label-spacer {
  width: 150px;
  flex-shrink: 0;
}

.time-markers {
  flex-grow: 1;
  display: grid;
  grid-template-columns: repeat(25, minmax(50px, 1fr));
}

.time-marker {
  text-align: center;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.85rem;
  padding: 0.25rem;
  border-left: 1px solid rgba(255, 0, 0, 0.2);
  font-weight: 500;
}

.days-container {
  position: relative;
  z-index: 2;
  border-radius: 8px;
  margin: 1rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  overflow: visible;
}

.day-container {
  display: flex;
  gap: 1rem;
  min-height: 300px;
  overflow: visible;
}

.day-label {
  width: 150px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #414a4a;
  border: 1px solid #76c4ae;
  border-radius: 4px;
}

.day-label-content {
  font-weight: bold;
  color: #ffffff;
  text-align: center;
  padding: 1rem;
  font-size: 1.2rem;
}

.day-content {
  flex-grow: 0;
  position: relative;
  border: 1px solid #76c4ae;
  border-radius: 4px;
  background: #414a4a;
  min-height: 200px;
  min-width: auto;
  overflow-x: auto;
  overflow-y: visible;
  cursor: grab;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.event-rows {
  position: relative;
  z-index: 1;
  overflow: visible;
}

.event-row {
  position: relative;
  height: 90px;
  margin-bottom: 0.1rem;
  overflow: visible;
}

.event-card {
  position: absolute;
  top: 8px;
  bottom: 8px;
  border-radius: 4px;
  height: 130px;
  border: 4px solid rgba(255, 0, 0, 0.0);
  transition: all 0.3s ease;
  z-index: 1;
}

.event-card:hover {
  transition: all 0.3s ease-in-out;
  transform: translateY(-2px);
  z-index: 999; /* Increased z-index when hovering */
  border-width: 2px;
}

.hover-card {
  position: absolute;
  background: #2d3434;
  border: 1px solid #76c4ae;
  padding: 1rem;
  border-radius: 4px;
  z-index: 1000;
  opacity: 0;
  transition: opacity 0.4s ease-in-out;
  pointer-events: none;
  width: 300px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.event-card:hover .hover-card {
  opacity: 1;
  pointer-events: auto;
}

.hover-card-content {
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  max-height: 400px; /* Maximum height for very long descriptions */
  overflow-y: auto;
}

.hover-description {
  font-size: 0.9rem;
  color: #ccc;
  line-height: 1.4;
}

/* Update positioning classes for fixed positioning */
.align-left {
  left: 0;
  top: 100%;
  transform: translate(-100%, -100%);
  margin-left: -10px;
}

.align-right {
  right: 0;
  top: 100%;
  transform: translate(100%, -100%);
  margin-right: -10px;
}

.align-bottom.align-left {
  top: auto;
  bottom: 0;
  transform: translate(-100%, 42%);
}

.align-bottom.align-right {
  top: auto;
  bottom: 0;
  transform: translate(100%, 42%);
}

.day-timeline {
  display: grid;
  border-bottom: 1px solid #76c4ae;
  margin-bottom: 0.5rem;
  padding-bottom: 0.25rem;
}

.timeline-hour {
  text-align: center;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.8rem;
  padding: 0.25rem;
  border-left: 1px solid #76c4ae;
  font-weight: 500;
  font-family: monospace;
  width: 100%;
}

.timeline-hour:first-child {
  border-left: none;
}

.time-grid {
  position: absolute;
  top: 2.5rem;
  left: 0;
  right: 0;
  bottom: 0;
  display: grid;
  pointer-events: none;
}

.time-grid-line {
  border-left: 1px solid #76c4ae23;
  height: 100%;
}

.event-content {
  transition: all 0.3s ease-in-out;
  padding: 0.6rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  background: rgba(200, 117, 69, 1);
}

.event-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;

  h3 {
    margin: 0;
    font-size: 0.9rem;
    font-weight: 600;
    color: #76c4ae;
  }
}

.event-content p {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.95);
  white-space: nowrap;
  overflow: hidden;
  letter-spacing: -1px;

  margin-bottom: 0.25rem;
  line-height: 1.2;
}

.hover-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: #76c4ae;
}

.hover-time {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: #ccc;
}

.legend-container {
  width: 104%;
  transform: translateX(-2%);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-bottom: 0.5rem;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  background: #414a4a;
  border: 1px solid #76c4ae;
  border-radius: 4px;
}

.legend-title {
  font-size: 1.5rem;
  color: #76c4ae;
  text-transform: uppercase;
  letter-spacing: 2px;
}

.legend-items {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 2rem;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #fff;
}

.legend-item .category-icon {
  font-size: 25px;
}

@media (max-width: 768px) {
  .legend-items {
    gap: 1rem;
    flex-direction: column;
  }
  
  .legend-container {
    padding: 0.75rem;
  }

  .day-label {
    width: 100px;
  }

  .day-label-content {
    font-size: 1rem;
  }
}

.bottom-links {
  width: 100%;
  display: flex;
  justify-content: center;
  gap: 4rem;
  padding: 4rem 0 2rem 0;
  margin-top: 2rem;
}

.nav-link {
  color: white;
  min-width: 100px;
  text-align: center;
  text-decoration: none;
  font-size: 0.7rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 2px;
  transition: color 0.3s ease;
}

.nav-link:hover {
  color: #e69b71;
}

@media (max-width: 768px) {
  .bottom-links {
    gap: 2rem;
  }

  .nav-link {
    font-size: 0.7rem;
  }
}

.big-highlight {
  width: 100%;
  min-height: 60vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(118, 196, 174, 0.05);
  border: 2px solid rgba(118, 196, 174, 0.2);
  border-radius: 8px;
  margin: 2rem 0;
  position: relative;
  overflow: hidden;
}

.big-highlight::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, 
    transparent 0%,
    rgba(118, 196, 174, 0.1) 50%,
    transparent 100%
  );
  animation: shine 6s linear infinite;
}

@keyframes shine {
  0% {
    transform: translateX(0%);
  }
  100% {
    transform: translateX(200%);
  }
}

.highlight-content {
  text-align: center;
  z-index: 1;
}

.highlight-content h2 {
  font-family: 'Airborne GP', sans-serif;
  font-size: 4rem;
  color: #76c4ae;
  text-transform: uppercase;
  letter-spacing: 8px;
  text-shadow: 0 0 20px rgba(118, 196, 174, 0.3);
}

@media (max-width: 768px) {
  .highlight-content h2 {
    font-size: 2.5rem;
    letter-spacing: 4px;
  }
}

@media (max-width: 576px) {
  .highlight-content h2 {
    font-size: 2rem;
    letter-spacing: 2px;
  }
}

.disabled-hour {
  display: none;
}

.disabled-grid {
  display: none;
}
</style>
